import type { Level1To10, TechBranch, TechnologyNode } from "../shared/game-types";
import { RESEARCH_DURATION_YEARS } from "./balance";

type TechRow = readonly [name: string, description: string];

const INDUSTRY: readonly TechRow[] = [
  ["基础航天工业", "化学燃料、基础冶金、模块化基地与初级轨道设施"],
  ["核工业", "铀分离、同位素浓缩、核热设备与高温合金"],
  ["轨道工业", "真空冶金、轨道造船、小行星自动采矿与离子推进制造"],
  ["极端环境工业", "高压采集、高温采矿、碳化物陶瓷与大型散热"],
  ["大规模自动化", "无人矿业网络、自动工厂、核脉冲船体与供应链调度"],
  ["聚变工业", "氘分离、氚增殖、Nb3Sn 超导与聚变船厂"],
  ["高级聚变工业", "氦-3 采集、REBCO、碳化硅与高速星际飞船"],
  ["反质子工业", "粒子加速器、反质子储存、极低温与高辐射工程"],
  ["反物质工业", "反氢制造、反物质储存与极高功率能源转换"],
  ["恒星级工业", "恒星能源阵列、超轻光帆与恒星系自动建设网络"],
];

const NAVIGATION: readonly TechRow[] = [
  ["化学推进", "液氧/甲烷发动机与低温推进剂储存"],
  ["固体核热推进", "固体堆芯核热发动机与辐射防护"],
  ["裂变电离子推进", "长期裂变电力、氙/氩推进剂与离子推进器"],
  ["气态铀堆芯推进", "气态堆芯、极端高温材料与大型散热"],
  ["裂变脉冲推进", "裂变脉冲单元、重型船体与深空补给"],
  ["氘氚聚变推进", "超导磁约束、氚增殖与初级星际航行"],
  ["氘氦-3 聚变推进", "低中子高级聚变与高速星际飞船"],
  ["反质子催化聚变", "反质子制造、储存与相对论前期推进"],
  ["反物质推进", "物质-反物质湮灭、极端防护与高速推进"],
  ["激光帆/光子推进", "恒星级外部光束、超轻光帆与光子系统"],
];

const MILITARY: readonly TechRow[] = [
  ["轨道防御", "轨道监视、常规导弹、动能拦截与基础点防御"],
  ["核动力巡逻", "核热拦截舰、预警雷达与辐射防护船体"],
  ["远距离交战", "高功率激光、远程导弹、电子干扰与诱饵"],
  ["高能防御", "多层碎片防护、主动热管理与无人战斗器"],
  ["重型舰队", "核脉冲重舰、轨道防御平台与长期后勤舰"],
  ["聚变舰队", "聚变拦截舰、电磁武器、高速导弹与粒子偏转"],
  ["光束网络", "跨行星火控、大型激光阵列与分布式监视"],
  ["反质子系统", "反质子动力舰、极高速拦截与先进诱饵"],
  ["反物质舰队", "恒星系拦截网络、极远传感与高速动能平台"],
  ["恒星系防御", "定向光束阵列、光帆拦截器与全系统调度"],
];

function buildBranch(branch: TechBranch, prefix: "I" | "N" | "M", rows: readonly TechRow[]): TechnologyNode[] {
  return rows.map(([name, description], index) => {
    const level = (index + 1) as Level1To10;
    const prerequisites: string[] = level === 1 ? [] : [`${prefix}${level - 1}`];
    if (branch === "navigation" && level > 1) prerequisites.push(`I${level}`);
    if (branch === "military" && level > 1) prerequisites.push(`I${Math.max(2, level - 1)}`);
    return {
      id: `${prefix}${level}`,
      branch,
      level,
      name,
      description: branch === "navigation" ? `${description}；两类运输船有效货舱 +${(level - 1) * 20}%` : description,
      cost: Math.round(90 * level ** 2.15),
      durationYears: RESEARCH_DURATION_YEARS[level],
      prerequisites,
      requiredEnergy: Math.max(1, level - 1) as Level1To10,
      requiredMaterials: Math.max(1, level - 1) as Level1To10,
    };
  });
}

/** The complete V1 I/N/M tree. IDs are stable (`I1`…`M10`). */
export const TECHNOLOGIES: readonly TechnologyNode[] = [
  ...buildBranch("industry", "I", INDUSTRY),
  ...buildBranch("navigation", "N", NAVIGATION),
  ...buildBranch("military", "M", MILITARY),
];

export const TECHNOLOGY_BY_ID: Readonly<Record<string, TechnologyNode>> = Object.fromEntries(
  TECHNOLOGIES.map((technology) => [technology.id, technology]),
);
